// Configuration for Product Types
export const PRODUCT_TYPE_META: Record<
  string,
  { label: string; color: string }
> = {
  product: {
    label: "Products",
    color: "#FF8000",
  },
  accessory: {
    label: "Accessories",
    color: "#167FFF",
  },
  replacement_parts: {
    label: "Replacement Parts",
    color: "#8A38F5",
  },
  default: {
    label: "-",
    color: "#6c757d", // Grey
  },
};

export enum FinishType {
  PRE_GALVANISED = "pre_galvanised",
  HOT_DIP_GALVANISED = "hot_dip_galvanised",
  POWDER_COATED = "powder_coated", // Assuming 'powder' meant Powder Coated based on previous context, updated key slightly for clarity or match your backend
}

export const formatFinishLabel = (value: string) => {
  return (
    value?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) || "-"
  );
};

export enum ProductFinishEnum {
  HOT_DIP_GALVANISED = "hot_dip_galvanised",
  PRE_GALVANISED = "pre_galvanised",
  // Add future finishes here
  POWDER_COATED = "powder",
  ZINC_PLATED = "zinc_plated",
  NON_STANDARD_COLOURS = "powder_coated_non_standard_colour",
}

// 2. The Single Source of Truth for UI Display
export const ProductFinishLabels: Record<ProductFinishEnum, string> = {
  [ProductFinishEnum.HOT_DIP_GALVANISED]: "Hot Dip Galvanised",
  [ProductFinishEnum.PRE_GALVANISED]: "Pre Galvanised",
  [ProductFinishEnum.POWDER_COATED]: "Powder Coated",
  [ProductFinishEnum.ZINC_PLATED]: "Zinc Plated",
  [ProductFinishEnum.NON_STANDARD_COLOURS]: "Powder Coated - Non-Standard Colour",
};

// 3. Helper to generate AntD Options array automatically
export const getFinishOptions = () => {
  return Object.values(ProductFinishEnum).map((value) => ({
    value: value,
    label: ProductFinishLabels[value],
  }));
};

export enum ProductDropEnum {
  STANDARD = "standard",
  CUSTOM = "custom",
  DROP_TBC = "drop_tbc",
}

export const ProductDropLabels: Record<ProductDropEnum, string> = {
  [ProductDropEnum.STANDARD]: "Standard Drop (35mm)",
  [ProductDropEnum.CUSTOM]: "Custom Drop",
  [ProductDropEnum.DROP_TBC]: "Drop TBC",
};

export const getDropOptions = () => {
  return Object.values(ProductDropEnum).map((value) => ({
    value: value,
    label: ProductDropLabels[value],
  }));
};

export enum ProductComponentTypeEnum {
  END_FRAME = "end_frame",
  EXTENSION_END_FRAME = "extension_end_frame",
  ROW_SPACERS = "row_spacers",
  CLOSED_BEAM = "closed_beam",
  OPEN_BEAM = "open_beam",
  PAND_D_BEAM = "pand_d_beam",
  LONGSPAN_BEAMS = "longspan_beams",
  BACKSTOP_BEAM = "backstop_beam",
  BEAMS_CW_ANGLE = "beams_cw_angle",
  DRIVE_IN_BEAMS = "drive_in_beams",
  BI_POLES = "bi_poles",
  MONO_POLES = "mono_poles",
  PROFILE = "uprights",
  AISLE_TIES = "aisle_ties",
  ARMS = "arms",
  RAILS = "rails",
  APR_ENDFRAMES = "apr_endframes",
  DRIVE_IN_ENDFRAMES = "drive_in_endframes",
  SHUTTLE_ENDFRAMES = "shuttle_endframes",
  BOLTLESS_SHELVING_FRAMES = "boltless_shelving_frames",
  DROPOVER_PANELS = "dropover_panels",
  LONGSPAN_SHELF = "longspan_shelf",
  SHELVES = "shelves",
  BACKS = "backs",
  LONGSPAN_ENDFRAMES = "longspan_endframes",
  Z_BEAMS = "z_beams",
  CHIPBOARD = "chipboard",
  // OTHER = "other",
}

export const ProductComponentTypeLabels: Record<
  ProductComponentTypeEnum,
  string
> = {
  [ProductComponentTypeEnum.END_FRAME]: "Endframes",
  [ProductComponentTypeEnum.EXTENSION_END_FRAME]: "Extension Endframes",
  [ProductComponentTypeEnum.ROW_SPACERS]: "Row Spacers",
  [ProductComponentTypeEnum.CLOSED_BEAM]: "Closed Beams",
  [ProductComponentTypeEnum.OPEN_BEAM]: "Open Beams",
  [ProductComponentTypeEnum.PAND_D_BEAM]: "P&D Beam",
  [ProductComponentTypeEnum.LONGSPAN_BEAMS]: "Longspan Beams",
  [ProductComponentTypeEnum.BACKSTOP_BEAM]: "Backstop Beam",
  [ProductComponentTypeEnum.BEAMS_CW_ANGLE]: "Beam CW Angle",
  [ProductComponentTypeEnum.DRIVE_IN_BEAMS]: "Drive In Beams",
  [ProductComponentTypeEnum.BI_POLES]: "Bi-Poles",
  [ProductComponentTypeEnum.MONO_POLES]: "Mono-Poles",
  [ProductComponentTypeEnum.PROFILE]: "Uprights",
  [ProductComponentTypeEnum.AISLE_TIES]: "Aisle Ties",
  [ProductComponentTypeEnum.ARMS]: "Arms",
  [ProductComponentTypeEnum.RAILS]: "Rails",
  [ProductComponentTypeEnum.APR_ENDFRAMES]: "APR Endframes",
  [ProductComponentTypeEnum.DRIVE_IN_ENDFRAMES]:
    "Drive-In/Drive-Through Endframes",
  [ProductComponentTypeEnum.SHUTTLE_ENDFRAMES]: "Shuttle Endframes",
  [ProductComponentTypeEnum.BOLTLESS_SHELVING_FRAMES]:
    "Boltless Shelving Frames",
  [ProductComponentTypeEnum.DROPOVER_PANELS]: "Dropover Panels",
  [ProductComponentTypeEnum.LONGSPAN_SHELF]: "Longspan Shelf",
  [ProductComponentTypeEnum.SHELVES]: "Shelves",
  [ProductComponentTypeEnum.BACKS]: "Backs",
  [ProductComponentTypeEnum.LONGSPAN_ENDFRAMES]: "Longspan Endframes",
  [ProductComponentTypeEnum.Z_BEAMS]: "Z Beams",
  [ProductComponentTypeEnum.CHIPBOARD]: "Chipboard",
  // [ProductComponentTypeEnum.OTHER]: "Other",
};

export enum MajorProductTypes {
  ENDFRAMES = "end_frames_products",
  BEAMS = "beams_products",
  SHELVES = "shelves_products",
}

export const getMajorProductType = (
  type: ProductComponentTypeEnum,
): MajorProductTypes | null => {
  const endFrames = [
    ProductComponentTypeEnum.END_FRAME,
    ProductComponentTypeEnum.EXTENSION_END_FRAME,
    ProductComponentTypeEnum.APR_ENDFRAMES,
    ProductComponentTypeEnum.DRIVE_IN_ENDFRAMES,
    ProductComponentTypeEnum.SHUTTLE_ENDFRAMES,
    ProductComponentTypeEnum.LONGSPAN_ENDFRAMES,
  ];

  const beamTypes = [
    ProductComponentTypeEnum.CLOSED_BEAM,
    ProductComponentTypeEnum.OPEN_BEAM,
    ProductComponentTypeEnum.DRIVE_IN_BEAMS,
    ProductComponentTypeEnum.Z_BEAMS,
  ];

  const shelvesTypes = [ProductComponentTypeEnum.SHELVES];

  if (endFrames.includes(type)) {
    return MajorProductTypes.ENDFRAMES;
  }
  if (beamTypes.includes(type)) {
    return MajorProductTypes.BEAMS;
  }
  if (shelvesTypes.includes(type)) {
    return MajorProductTypes.SHELVES;
  }

  return null;
};

export const getProductComponentTypeOptions = () => {
  return Object.values(ProductComponentTypeEnum).map((value) => ({
    value: value,
    label: ProductComponentTypeLabels[value],
  }));
};

export enum ProductExportImportStatusEnum {
  IN_PROGRESS = "in_progress",
  COMPLETED = "completed",
  CANCEL = "cancel",
  FAILED = "failed",
  IN_QUEUE = "in_queue",
}

export const exportImportStatusClasess = (status: string) => {
  switch (status) {
    case ProductExportImportStatusEnum.IN_PROGRESS:
      return "designPendingStatus";
    case ProductExportImportStatusEnum.COMPLETED:
      return "approvedStatus";
    case ProductExportImportStatusEnum.CANCEL:
      return "expiredStatus";
    case ProductExportImportStatusEnum.FAILED:
      return "expiredStatus";
    case ProductExportImportStatusEnum.IN_QUEUE:
      return "sentStatus";
    default:
      return "sentStatus";
  }
};

export const exportImportStatusLabel = (status: string) => {
  switch (status) {
    case ProductExportImportStatusEnum.IN_PROGRESS:
      return "In Progress";
    case ProductExportImportStatusEnum.COMPLETED:
      return "Completed";
    case ProductExportImportStatusEnum.CANCEL:
      return "Cancelled";
    case ProductExportImportStatusEnum.FAILED:
      return "Failed";
    case ProductExportImportStatusEnum.IN_QUEUE:
      return "In Queue";
    default:
      return "";
  }
};

export const getProductImportActionLabel = (batch: any) => {
  if (!batch) return "Process";

  if (batch?.import_type === "product_variant_import") {
    return "Import";
  }

  if (batch?.import_type === "product_variant_generate") {
    switch (batch?.type) {
      case "add":
        return "Generate";
      case "edit":
        return "Update";
      case "regenerate":
        return "Regenerate";
      default:
        return "Generate";
    }
  }

  switch (batch?.type) {
    case "add":
      return "Generate";
    case "edit":
      return "Update";
    case "regenerate":
      return "Regenerate";
    default:
      return "Process";
  }
};

export const PRODUCT_SCROLL_KEY = "products_scroll_top";
export const PRODUCT_FILTER_KEY = "products_filters";
export const PRODUCT_REFETCH_KEY = "products_needs_refetch";
export const PRODUCT_REGENERATE_REFETCH_KEY = "product_regenerate_refetch";
export const PRODUCT_DETAILS_SCROLL_KEY = "productDetailsScrollPosition";
export const PRODUCT_DETAILS_VARIANT_SCROLL_KEY =
  "productDetailsVariantScrollPosition";
export const PRODUCT_DETAILS_REFETCH_KEY = "productDetailsRefetch";
export const PRODUCT_ACTIVE_VIEW_KEY = "product_active_view";
// --------------------------------------------------------------
//Label Variables Helpers
export interface IVariableArrangementItem {
  key: string;
  prefix: string;
}

export interface IVariableArrangement {
  as_dimensions: IVariableArrangementItem[];
  as_per_additional_fields: IVariableArrangementItem[];
  as_per_finishing: IVariableArrangementItem[];
  as_per_custom: IVariableArrangementItem[];
}

export const DEFAULT_LABEL_ARRANGEMENT: IVariableArrangement = {
  as_dimensions: [
    { key: "height", prefix: "{value}mm (H)" },
    { key: "front_upright_height", prefix: "{value}mm (Front Upright H)" },
    { key: "rear_upright_height", prefix: "{value}mm (Rear Upright H)" },
    { key: "reinforced_height", prefix: "{value}mm (Reinforced H)" },
    { key: "width", prefix: "{value}mm (W)" },
    { key: "depth", prefix: "{value}mm (D)" },
    { key: "length", prefix: "{value}mm (L)" },
    { key: "thickness", prefix: "{value}mm Thick" },
    { key: "gauge", prefix: "{value}mm Gauge" },
    { key: "level_width", prefix: "to suit {value}mm (W)" },
    { key: "level_depth", prefix: "{value}mm (D) Levels" },
  ],
  as_per_additional_fields: [
    { key: "bolts_per_footplate", prefix: "{value} Bolts per Footplate" },
    { key: "dividers_per_shelf", prefix: "{value} Dividers per Shelf" },
    { key: "connecting_points", prefix: "{value} Connecting Points" },
    { key: "holes_drilled", prefix: "{value} Holes Drilled" },
    { key: "first_beam_level", prefix: "a 1st Beam Level of {value}mm" },
    { key: "frame_type", prefix: "{value}" },
    { key: "foot_type", prefix: "{value}" },
    { key: "shelf_reinforcers", prefix: "{value} Shelf Reinforcers" },
    { key: "crossties", prefix: "{value} Crossties per Frame" },
    { key: "pallet_supports", prefix: "{value} Pallet Supports" },
    { key: "chipboard_supports", prefix: "{value} Chipboard Supports" },
    { key: "drop", prefix: "Drop of {value}mm" },
    { key: "first_shelf_level", prefix: "a 1st Shelf Level of {value}mm" },
    // { key: "v_type", prefix: "Type {value}" },
  ],
  as_per_custom: [{ key: "v_type", prefix: "{value}" }],
  as_per_finishing: [
    { key: "finish", prefix: "{value}" },
    { key: "color", prefix: " - {value}" },
    { key: "secondary_finish", prefix: "{value}" },
    { key: "secondary_color", prefix: " - {value}" },
  ],
};

export enum AdditionalProductFieldsEnum {
  MAX_LOAD_PER_BAY = "max_load_per_bay",
  MAX_LOAD_PER_PAIR = "max_load_per_pair",
  MAX_LOAD_PER_SHELF = "max_load_per_shelf",
}

export const AdditionalProductFieldsLabels: Record<
  AdditionalProductFieldsEnum,
  string
> = {
  [AdditionalProductFieldsEnum.MAX_LOAD_PER_BAY]: "Max Load per Bay",
  [AdditionalProductFieldsEnum.MAX_LOAD_PER_PAIR]: "Max Load per Pair",
  [AdditionalProductFieldsEnum.MAX_LOAD_PER_SHELF]: "Max Load per Shelf",
};

export const generateAdditionalProductFieldsOptions = () => {
  return Object.values(AdditionalProductFieldsEnum).map((value) => ({
    value: value,
    label: AdditionalProductFieldsLabels[value],
  }));
};

export enum PriceCalculationEnum {
  CALCULATION = "calculation",
  MANUAL_IMPORT = "manual_import",
  NOT_SET = "not_set",
}

export const PriceCalculationLabels: Record<PriceCalculationEnum, string> = {
  [PriceCalculationEnum.CALCULATION]: "Calculation",
  [PriceCalculationEnum.MANUAL_IMPORT]: "Manual Import",
  [PriceCalculationEnum.NOT_SET]: "Not Set",
};

export const generatePriceCalculationOptions = () => {
  return Object.values(PriceCalculationEnum).map((value) => ({
    value: value,
    label: PriceCalculationLabels[value],
  }));
};

